import { TIME } from '@shared/src/utils/time';
import { extractVideoId } from '@shared/src/utils/youtube';
import { VideoInfo } from '@frontend/auction/src/services/VideoInfoService';
import { debounce } from '@shared/src/utils/debounce';

const resizeTextarea = (textarea: HTMLTextAreaElement) => {
  textarea.style.height = 'auto';

  const newHeight = textarea.scrollHeight + (textarea.offsetHeight - textarea.clientHeight);

  textarea.style.height = newHeight + 'px';
};

export class SPAListItem extends HTMLDivElement {
  static onReady = customElements.whenDefined('spa-list-item');

  private nameElement: HTMLTextAreaElement | null = null;
  private sumElement: HTMLInputElement | null = null;

  constructor() {
    super();

    this.setAttribute('is', 'spa-list-item');
  }

  get name() {
    return this.nameElement?.value || '';
  }

  set name(value) {
    if (this.nameElement) {
      this.nameElement.value = value;

      resizeTextarea(this.nameElement);
    }
  }

  get sum() {
    return +(this.sumElement?.value || '') || 0;
  }

  set sum(value) {
    if (this.sumElement) {
      this.sumElement.value = value !== 0 ? value.toString() : '';
    }
  }

  initElements = () => {
    const remove =
      this.querySelector<HTMLButtonElement>('.remove') ??
      (() => {
        const remove = document.createElement('button');

        remove.className = 'spa-button spa-dark spa-40 spa-rounded remove';
        remove.innerHTML = "<svg height='24' width='24'><use xlink:href='#icon-trash'></use></svg>";

        return remove;
      })();

    const name =
      this.querySelector<HTMLTextAreaElement>('.name') ??
      (() => {
        const name = document.createElement('textarea');

        name.className = 'name spa-textarea';
        name.placeholder = 'Название';
        name.spellcheck = false;
        name.rows = 1;

        return name;
      })();

    name.addEventListener('paste', e => {
      if (name.value !== '') {
        return;
      }

      const pasted = e.clipboardData?.getData('text') ?? '';

      if (!pasted) {
        return;
      }

      const videoId = extractVideoId(pasted);

      if (videoId) {
        VideoInfo.get(pasted).then(result => {
          if (result) {
            const minutes = result.duration / TIME.MINUTE;

            const price = Math.max(Math.round(minutes / 15), 1) * 2500;

            const value = `${result.title}\n${result.channel} (${Math.floor(
              minutes
            )} мин - ${price})`;

            name.value = value;

            resizeTextarea(name);
          }
        });
      }
    });

    const sum =
      this.querySelector<HTMLInputElement>('.sum') ??
      (() => {
        const sum = document.createElement('input');

        sum.className = 'sum spa-input';
        sum.placeholder = 'Сумма';
        sum.type = 'number';

        return sum;
      })();

    const diff =
      this.querySelector<HTMLInputElement>('.diff') ??
      (() => {
        const diff = document.createElement('input');

        diff.className = 'diff spa-input';
        diff.placeholder = '+ / -';
        diff.type = 'number';
        diff.setAttribute('list', 'sums');

        return diff;
      })();

    const add =
      this.querySelector<HTMLButtonElement>('.add') ??
      (() => {
        const add = document.createElement('button');

        add.className = 'add spa-button spa-dark spa-40 spa-rounded';
        add.innerText = '+';

        return add;
      })();

    [remove, name, sum, diff, add].forEach(element => this.appendChild(element));

    this.nameElement = name;
    this.sumElement = sum;

    return { remove, name, sum, diff, add };
  };

  emitRemove = () => {
    this.dispatchEvent(new CustomEvent('remove', { bubbles: true, composed: true }));
  };

  emitNameChange = () => {
    this.dispatchEvent(new CustomEvent('namechange', { bubbles: true, composed: true }));
  };

  emitSumChange = () => {
    this.dispatchEvent(new CustomEvent('sumchange', { bubbles: true, composed: true }));
  };

  connectedCallback() {
    const { remove, name, sum, diff, add } = this.initElements();

    if (remove === null || name === null || sum === null || diff === null || add === null) {
      return;
    }

    remove.onclick = () => {
      this.emitRemove();
    };

    name.onchange = () => {
      this.emitNameChange();
    };

    name.oninput = debounce(() => resizeTextarea(name), 250, { maxWait: 1000, leading: true });

    name.onpaste = event => {
      if (event.clipboardData) {
        const data = event.clipboardData.getData('text/plain');

        if (name.value === '' && data.length > 0) {
          event.preventDefault();

          name.value = data.replace(/\n\n\n+/, '\n\n').trim();
        }

        this.emitNameChange();

        resizeTextarea(name);
      }
    };

    sum.onchange = () => {
      this.emitSumChange();
    };

    add.onclick = () => {
      const diff_ = +diff.value || 0;

      if (diff_ !== 0) {
        const prevSum = +sum.value || 0;
        const nextSum = prevSum + diff_;

        sum.value = nextSum.toString();

        diff.value = '';

        this.emitSumChange();
      }
    };
  }
}

customElements.define('spa-list-item', SPAListItem, { extends: 'div' });
